import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import RhythmStaff from '@/components/feature/RhythmStaff';
import RhythmSummaryPanel from './RhythmSummaryPanel';
import RhythmLiveSummary from './RhythmLiveSummary';
import PianoKeyboard from '@/components/feature/PianoKeyboard';
import MidiPanel from '@/components/feature/MidiPanel';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import ProgressBar from '@/components/base/ProgressBar';
import { useRhythmSession } from '@/hooks/useRhythmSession';
import { useMidiInput } from '@/hooks/useMidiInput';
import { getNote } from '@/lib/music/notes';
import { DURATIONS, durationBeatLabel } from '@/lib/music/rhythm';
import {
  rhythmAccuracy,
  type ExerciseSummary,
  type RhythmConfig,
  type RhythmScore,
} from '@/lib/exercise/rhythmEngine';
import { assessRhythmAttempt, RHYTHM_ITEM_LABEL, type RhythmItemKey } from '@/lib/exercise/rhythmSummary';
import { RHYTHM_ACCURACY_GOAL } from '@/lib/progress/gating';
import type { NoteStatsMap } from '@/lib/exercise/engine';
import { nextLessonId, type Lesson } from '@/data/lessons';

interface RhythmPracticeProps {
  lesson: Lesson;
  noteStats: NoteStatsMap;
  onNoteResult: (noteId: string, correct: boolean) => void;
  onFinish: (accuracy: number, score: RhythmScore, history: ExerciseSummary[]) => void;
  onAbort: (score: RhythmScore, history: ExerciseSummary[]) => void;
}

function RhythmPracticeInner({
  lesson,
  config,
  noteStats,
  onNoteResult,
  onFinish,
  onAbort,
}: RhythmPracticeProps & { config: RhythmConfig }) {
  const session = useRhythmSession(config, {
    noteStats,
    onNoteResult,
    onFinish: (score, history) => onFinish(rhythmAccuracy(score), score, history),
    onAbort,
  });

  const midiInput = useMidiInput(
    (midi) => session.handleKey(midi),
    (midi) => session.handleKeyUp(midi),
  );

  const { startMidi, endMidi } = useMemo(() => {
    const midis = config.notes.map((id) => getNote(id).midi);
    const minMidi = midis.length > 0 ? Math.min(...midis) : 60;
    const maxMidi = midis.length > 0 ? Math.max(...midis) : 60;
    return { startMidi: Math.min(48, minMidi - 2), endMidi: Math.max(72, maxMidi + 2) };
  }, [config.notes]);

  const currentEvent = session.exercise.events[session.currentEventIndex];
  const expectedMidi =
    currentEvent && currentEvent.kind === 'note' ? getNote(currentEvent.noteId).midi : null;

  const symbolList = [
    ...config.figures.map((id) => ({
      id,
      name: DURATIONS[id].ptName,
      label: durationBeatLabel(id),
      kind: 'note' as const,
    })),
    ...(config.rests ?? []).map((id) => ({
      id: `rest-${id}`,
      name: `Pausa de ${DURATIONS[id].ptName.toLowerCase()}`,
      label: durationBeatLabel(id),
      kind: 'rest' as const,
    })),
  ];

  const hasRests = (config.rests ?? []).length > 0;
  const hasTies = config.ties === true;
  const measuresVoice = config.responseMode === 'on-beat';

  const assessmentContext = { hasRests, hasTies, measuresDuration: measuresVoice };
  const assessment = assessRhythmAttempt(session.score, assessmentContext);

  // Critério explícito de domínio, montado conforme o conteúdo da aula:
  // só entram os itens que a aula realmente cobra. O desbloqueio exige TODOS
  // eles sem erro — a precisão em % é apenas informativa.
  const criteria: { key: RhythmItemKey; text: string }[] = [];
  if (config.responseMode === 'on-beat') {
    criteria.push({ key: 'note', text: 'Tocar a altura certa (a tecla escrita na pauta)' });
  }
  criteria.push({
    key: 'attack',
    text:
      config.responseMode === 'pulse'
        ? 'Marcar cada pulso no tempo'
        : 'Atacar no pulso (sem cedo nem atrasado)',
  });
  if (measuresVoice) criteria.push({ key: 'duration', text: 'Segurar a nota até perto do fim da figura' });
  if (hasRests) criteria.push({ key: 'rest', text: 'Ficar em silêncio nas pausas' });
  if (hasTies) criteria.push({ key: 'tie', text: 'Manter a ligadura com um único ataque' });

  const stats = [
    { label: 'No pulso', value: session.score.correct, color: 'text-accent-700 bg-accent-100', icon: 'ri-check-double-line' },
    ...(hasRests
      ? [
          {
            label: 'Silêncio mantido',
            value: session.score.restsHeld,
            color: 'text-accent-700 bg-accent-100',
            icon: 'ri-pause-circle-line',
          },
        ]
      : []),
    ...(hasTies
      ? [
          {
            label: 'Ligaduras mantidas',
            value: session.score.tiesHeld,
            color: 'text-accent-700 bg-accent-100',
            icon: 'ri-link',
          },
        ]
      : []),
    { label: 'Cedo', value: session.score.early, color: 'text-primary-700 bg-primary-100', icon: 'ri-arrow-up-line' },
    { label: 'Atrasado', value: session.score.late, color: 'text-primary-700 bg-primary-100', icon: 'ri-arrow-down-line' },
    { label: 'Nota errada', value: session.score.wrongNote, color: 'text-secondary-700 bg-secondary-100', icon: 'ri-close-line' },
    ...(measuresVoice
      ? [
          {
            label: 'Duração cortada',
            value: session.score.durationBreak,
            color: 'text-secondary-700 bg-secondary-100',
            icon: 'ri-hourglass-line',
          },
        ]
      : []),
    ...(hasRests
      ? [
          {
            label: 'Falta de pausa',
            value: session.score.restIntrusion,
            color: 'text-secondary-700 bg-secondary-100',
            icon: 'ri-volume-mute-line',
          },
        ]
      : []),
    ...(hasTies
      ? [
          {
            label: 'Reataque na ligadura',
            value: session.score.tieReattack,
            color: 'text-secondary-700 bg-secondary-100',
            icon: 'ri-close-circle-line',
          },
        ]
      : []),
  ];

  const primaryLabel =
    session.phase === 'idle'
      ? 'Começar'
      : session.phase === 'playing'
        ? 'Pausar'
        : session.phase === 'paused'
          ? 'Continuar'
          : 'Praticar novamente';

  const next = nextLessonId(lesson.id);

  const failedLabels = assessment.failedKeys.map((key) => RHYTHM_ITEM_LABEL[key]);
  const unmeasuredLabels = assessment.notEvaluatedKeys.map((key) => RHYTHM_ITEM_LABEL[key]);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex flex-col gap-6">
        {/* Explicação */}
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
          <div className="flex items-center gap-2">
            <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center text-primary-700" />
            <span className="font-label text-sm font-bold text-primary-800">Antes de praticar</span>
          </div>
          <p className="mt-2 text-sm text-foreground-800">{lesson.explanation}</p>
          {lesson.hint ? (
            <p className="mt-2 text-sm text-foreground-600">
              <span className="font-semibold">Dica:</span> {lesson.hint}
            </p>
          ) : null}
          <div className="mt-3 rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-700">
            <span className="font-semibold">Critério de domínio desta aula:</span> concluir a
            prática com <strong>pelo menos {RHYTHM_ACCURACY_GOAL}%</strong> em cada item avaliado.
            Se ficar abaixo da meta, o aluno pratica novamente até recuperar o domínio.
            <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
              {criteria.map((item) => (
                <li key={item.key} className="inline-flex items-center gap-1.5">
                  <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center text-accent-600" />
                  {item.text}
                </li>
              ))}
            </ul>
            <span className="mt-1.5 block">
              A meta de {RHYTHM_ACCURACY_GOAL}% é o ponto de domínio. O sistema aceita raríssimos
              erros e usa novas repetições para confirmar que o conteúdo realmente foi fixado,
              sempre com tolerância adequada para iniciantes.
            </span>
            {measuresVoice ? (
              <span className="mt-1.5 block text-foreground-600">
                <i className="ri-information-line mr-1" />
                A duração é medida quando o aparelho informa o fim da nota: soltar a tecla no teclado
                virtual ou o Note Off do MIDI. A tecla precisa ficar pressionada até perto do fim da
                figura (ao menos 80% dela, e nunca mais de um quarto de pulso cedo demais). Se o
                aparelho não informar o fim da nota, a duração fica “não avaliada” e a aula não conta
                como dominada.
              </span>
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {symbolList.map((symbol) => (
              <span
                key={symbol.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-2.5 py-0.5 text-xs font-label font-semibold text-foreground-800"
              >
                <i
                  className={
                    symbol.kind === 'rest'
                      ? 'ri-pause-line w-4 h-4 flex items-center justify-center text-secondary-600'
                      : 'ri-music-2-line w-4 h-4 flex items-center justify-center text-primary-600'
                  }
                />
                {symbol.name} · {symbol.label}
              </span>
            ))}
          </div>
        </div>

        {/* Exercício */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Badge tone="primary" icon="ri-timer-line">
              {session.responseMode === 'pulse'
                ? 'Marque o pulso com qualquer tecla'
                : 'Toque a nota no pulso'}
            </Badge>
            <span className="text-xs font-label font-semibold text-foreground-500">
              Exercício {Math.min(session.exerciseIndex + 1, session.totalExercises)} de{' '}
              {session.totalExercises} · {session.effectiveBpm} BPM
            </span>
          </div>

          {/* Pauta */}
          <div className="mt-4 rounded-lg bg-background-100 p-3 md:p-4">
            <RhythmStaff
              events={session.exercise.events}
              beatsPerMeasure={session.beatsPerMeasure}
              timeSignature={session.timeSignature}
              currentIndex={session.currentEventIndex}
              marks={session.marks}
              restGlow={config.restGlow}
              space={26}
            />
          </div>

          {config.restGlow ? (
            <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-foreground-600">
              <i className="ri-volume-mute-line w-4 h-4 flex items-center justify-center text-accent-600" />
              A pausa acende na pauta enquanto você mantém o silêncio.
            </p>
          ) : null}

          {/* Pulsos */}
          <div className="mt-4 flex items-center justify-center gap-2.5 md:gap-4">
            {Array.from({ length: session.beatsPerMeasure }).map((_, index) => {
              const beatNumber = index + 1;
              const active =
                (session.phase === 'playing' && session.currentBeat === beatNumber) ||
                (session.countInBeat === beatNumber && session.phase === 'playing');
              const accent = index === 0;
              return (
                <div
                  key={beatNumber}
                  className={`w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full border font-heading text-base font-bold transition-all duration-100 ${
                    active
                      ? accent
                        ? 'scale-110 border-accent-500 bg-accent-500 text-background-50'
                        : 'scale-110 border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-100 text-foreground-500'
                  }`}
                >
                  {beatNumber}
                </div>
              );
            })}
          </div>

          {session.countInBeat > 0 && session.phase === 'playing' ? (
            <p className="mt-2 text-center text-sm font-label font-semibold text-primary-700">
              Contagem inicial: {session.countInBeat}
            </p>
          ) : null}

          {/* Feedback */}
          <div className="mt-4 min-h-[52px]">
            {session.feedback ? (
              <div
                className={`flex items-start gap-3 rounded-lg px-4 py-3 text-sm animate-pop ${
                  session.feedback.type === 'correct'
                    ? 'bg-accent-100 text-accent-900'
                    : session.feedback.type === 'wrong'
                      ? 'bg-secondary-100 text-secondary-900'
                      : 'bg-primary-100 text-primary-900'
                }`}
              >
                <i
                  className={`${
                    session.feedback.type === 'correct'
                      ? 'ri-check-line'
                      : session.feedback.type === 'wrong'
                        ? 'ri-information-line'
                        : 'ri-timer-line'
                  } w-5 h-5 mt-0.5 flex items-center justify-center`}
                />
                <span>{session.feedback.message}</span>
              </div>
            ) : (
              <p className="text-sm text-foreground-500">
                {session.phase === 'playing'
                  ? 'Siga o metrônomo e toque cada figura no lugar certo.'
                  : 'Clique em “Começar” e acompanhe a contagem inicial 1, 2, 3, 4.'}
              </p>
            )}
          </div>

          {/* Teclado */}
          <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-3">
            <PianoKeyboard
              startMidi={startMidi}
              endMidi={endMidi}
              highlightMidis={expectedMidi !== null ? [expectedMidi] : []}
              showNoteNames={config.notes.length <= 5}
              silent
              onKeyDown={(midi) => session.handleKey(midi)}
              onKeyUp={(midi) => session.handleKeyUp(midi)}
              disabled={session.phase === 'done'}
            />
            <p className="mt-2 text-center text-xs text-foreground-500 md:hidden">
              Deslize o teclado para os lados para ver todas as teclas.
            </p>
          </div>

          {/* Controles */}
          <div className="mt-5 flex flex-wrap gap-2.5">
            <Button variant="outline" icon="ri-volume-up-line" onClick={session.playExample}>
              Ouvir exemplo
            </Button>
            <Button variant="primary" icon="ri-play-line" onClick={session.togglePrimary}>
              {primaryLabel}
            </Button>
            <Button
              variant="outline"
              icon="ri-restart-line"
              disabled={session.phase !== 'playing' && session.phase !== 'paused'}
              onClick={session.restartExercise}
            >
              Recomeçar
            </Button>
            <Button variant="ghost" icon="ri-arrow-go-back-line" onClick={session.start}>
              Reiniciar aula
            </Button>
          </div>

          {session.phase === 'done' ? (
            <div
              className={`mt-5 flex flex-col items-center rounded-lg border p-5 text-center ${
                assessment.mastered
                  ? 'border-accent-200 bg-accent-50'
                  : assessment.errorFree
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-secondary-200 bg-secondary-50'
              }`}
            >
              <span
                className={`w-14 h-14 flex items-center justify-center rounded-full ${
                  assessment.mastered
                    ? 'bg-accent-100 text-accent-600'
                    : assessment.errorFree
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-secondary-100 text-secondary-700'
                }`}
              >
                <i
                  className={`${
                    assessment.mastered
                      ? 'ri-medal-line'
                      : assessment.errorFree
                        ? 'ri-check-double-line'
                        : 'ri-refresh-line'
                  } text-2xl`}
                />
              </span>
              <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">
                {assessment.mastered
                  ? 'Aula dominada!'
                  : assessment.errorFree
                    ? 'Execução sem erros!'
                    : 'Ainda com erros'}
              </h3>
              <p className="mt-1 max-w-md text-sm text-foreground-700">
                Você completou {session.totalExercises} exercícios com {session.accuracy}% de
                precisão.{' '}
                {assessment.mastered
                  ? `Domínio confirmado em pelo menos ${RHYTHM_ACCURACY_GOAL}%: a próxima aula foi desbloqueada.`
                  : assessment.errorFree
                    ? `Sem erros nos itens medidos, mas ${unmeasuredLabels.join(' e ')} não pôde ser medido neste aparelho — por isso a aula ainda não conta como dominada.`
                    : `Ainda abaixo de ${RHYTHM_ACCURACY_GOAL}% em: ${failedLabels.join(', ')}. Continue praticando para liberar a próxima etapa.`}
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Button
                  variant={assessment.mastered ? 'outline' : 'primary'}
                  icon="ri-refresh-line"
                  onClick={session.start}
                >
                  Refazer aula
                </Button>
                {next && assessment.mastered ? (
                  <Link to={`/aulas/${next}`} className="contents">
                    <Button variant="primary" icon="ri-arrow-right-line">
                      Próxima aula
                    </Button>
                  </Link>
                ) : null}
              </div>
              {!assessment.mastered ? (
                <p className="mt-3 max-w-md text-xs text-foreground-500">
                  Dica: ligue “Velocidade reduzida” e refaça mais devagar — dominar devagar é o
                  caminho para dominar no tempo.
                </p>
              ) : null}
            </div>
          ) : null}

          {session.phase === 'done' ? (
            <RhythmSummaryPanel
              score={session.score}
              history={session.history}
              hasRests={hasRests}
              hasTies={hasTies}
              hasDuration={measuresVoice}
              accuracy={session.accuracy}
            />
          ) : null}
        </div>
      </div>

      {/* Lateral */}
      <aside className="flex flex-col gap-6">
        {session.phase === 'playing' || session.phase === 'paused' ? (
          <RhythmLiveSummary
            score={session.score}
            hasRests={hasRests}
            hasTies={hasTies}
            hasDuration={measuresVoice}
          />
        ) : null}

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="flex items-center justify-between">
            <span className="font-label text-sm font-bold text-foreground-800">Desempenho</span>
            <span className="font-heading text-lg font-bold text-foreground-950">
              {session.accuracy}%
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {stats.map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className={`w-8 h-8 flex items-center justify-center rounded-md ${item.color} shrink-0`}>
                  <i className={`${item.icon} text-sm`} />
                </span>
                <div className="min-w-0">
                  <div className="font-heading text-base font-bold leading-none text-foreground-950">
                    {item.value}
                  </div>
                  <div className="text-[11px] text-foreground-500 truncate">{item.label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between text-xs text-foreground-600">
              <span className="font-label font-semibold">Progresso da aula</span>
              <span>
                {Math.min(session.exerciseIndex + 1, session.totalExercises)} / {session.totalExercises}
              </span>
            </div>
            <ProgressBar
              value={Math.min(session.exerciseIndex + 1, session.totalExercises)}
              max={session.totalExercises}
              tone="primary"
            />
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="font-label text-sm font-bold text-foreground-800">Andamento</span>
          <div className="mt-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-foreground-600">BPM</span>
              <span className="font-heading text-base font-bold text-primary-700">
                {session.effectiveBpm}
                {session.slowMode ? (
                  <span className="ml-2 text-[11px] font-label font-semibold text-foreground-500">
                    reduzido
                  </span>
                ) : null}
              </span>
            </div>
            <input
              type="range"
              min={40}
              max={140}
              step={4}
              value={session.bpm}
              disabled={session.phase === 'playing'}
              onChange={(event) => session.setBpm(Number(event.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-background-200 accent-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <label className="mt-3 flex items-center gap-2 text-sm font-label font-semibold text-foreground-700 cursor-pointer">
              <input
                type="checkbox"
                checked={session.slowMode}
                disabled={session.phase === 'playing'}
                onChange={(event) => session.setSlowMode(event.target.checked)}
                className="h-4 w-4 accent-primary-500"
              />
              Velocidade reduzida
            </label>
            {session.phase === 'playing' ? (
              <p className="mt-2 text-xs text-foreground-500">
                Pause o exercício para ajustar o andamento.
              </p>
            ) : null}
          </div>
        </div>

        <MidiPanel
          supported={midiInput.supported}
          status={midiInput.status}
          devices={midiInput.devices}
          selectedId={midiInput.selectedId}
          onSelect={midiInput.setSelectedId}
          onConnect={midiInput.connect}
          onDisconnect={midiInput.disconnect}
        />
      </aside>
    </div>
  );
}

export default function RhythmPractice(props: RhythmPracticeProps) {
  const { rhythm } = props.lesson;
  if (!rhythm) return null;
  return <RhythmPracticeInner {...props} config={rhythm} />;
}